import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Text } from '@/Components/ui/typography';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';
import { getColor } from '@/config/theme';
import { cn } from '@/lib/utils';

export default function RecruiterLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useRecruiterAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const res = await login({ email, password });
      if (res && res.needs_profile) {
        navigate('/recruiter/complete-profile');
      } else {
        navigate('/recruiter/dashboard');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div className="relative group">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within:text-primary" />
          <Input
            type="email"
            placeholder="Email"
            aria-label="Email"
            className="pl-10 h-10 transition-all"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <div className="relative group">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10 transition-colors group-focus-within:text-primary" />
            <Input
              type="password"
              placeholder="Password"
              aria-label="Password"
              className="pl-10 h-10 transition-all"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center justify-end">
            <Link 
              to="/hirewithus/forgot-password" 
              className={cn(
                "text-sm text-muted-foreground hover:text-primary",
                "transition-colors underline-offset-4 hover:underline"
              )}
            >
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
          <Text size="sm" variant="destructive" className="text-destructive">
            {errorMessage}
          </Text>
        </div>
      )}

      <Button 
        type="submit" 
        variant="default"
        size="default"
        className="w-full h-11 font-semibold shadow-sm hover:shadow-md transition-all duration-200"
        style={{
          backgroundColor: getColor('primary'),
          color: getColor('primaryForeground'),
          borderColor: getColor('primary')
        }}
        disabled={loading}
      >
        {loading ? 'Logging in...' : 'Login Now'}
      </Button>
    </form>
  );
}
